import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { API_URL, getHeaders, Spinner, Empty } from './shared';
import logger from '../../utils/logger';

export function PortfolioAccessRequests({ adminRole }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter !== 'all' ? `${API_URL}/api/admin/portfolio-access?status=${filter}` : `${API_URL}/api/admin/portfolio-access`;
      const res = await fetch(url, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed');
      setRequests((await res.json()).requests || []);
    } catch (err) { logger.error(err); toast.error('Failed to load access requests'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const act = async (reqId, action, payload = {}) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/portfolio-access/${reqId}/${action}`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify(payload),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed'); }
      toast.success(`Request ${action}d`);
      fetch_();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <Spinner text="Loading access requests..." />;

  const colors = {
    pending: 'bg-yellow-600/20 text-yellow-400 border-yellow-600/30',
    approved: 'bg-green-600/20 text-green-400 border-green-600/30',
    denied: 'bg-red-600/20 text-red-400 border-red-600/30',
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        {['all', 'pending', 'approved', 'denied'].map(f => (
          <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-lg text-xs font-medium transition ${filter === f ? 'bg-green-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>
      {requests.length === 0 ? <Empty text="No access requests" /> : (
        <div className="space-y-3">
          {requests.map((req, idx) => (
            <div key={req.request_id || `req-${idx}`} data-testid={`access-request-${req.request_id}`} className="bg-gray-800 border border-gray-700 rounded-xl p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-white">{req.requester_name}</span>
                    <span className="text-xs text-gray-500">({req.requester_role})</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${colors[req.status] || 'bg-gray-700 text-gray-400'}`}>{req.status}</span>
                    {req.urgency === 'critical' && <span className="text-xs bg-red-600/20 text-red-400 px-2 py-0.5 rounded-full">Critical</span>}
                    {req.urgency === 'high' && <span className="text-xs bg-yellow-600/20 text-yellow-400 px-2 py-0.5 rounded-full">High</span>}
                  </div>
                  <p className="text-xs text-gray-500">Target: {req.target_user_name || req.target_user_id}</p>
                  <p className="text-sm text-gray-300">{req.reason}</p>
                  {req.requested_at && <p className="text-xs text-gray-500">{new Date(req.requested_at).toLocaleString()}{req.is_overdue && <span className="text-yellow-400 font-medium ml-2">Overdue (24h+) - Backup approval available</span>}</p>}
                  {req.expires_at && <p className="text-xs text-green-400">Expires: {new Date(req.expires_at).toLocaleString()}</p>}
                </div>
                {req.status === 'pending' && adminRole === 'owner' && (
                  <div className="flex gap-2 shrink-0">
                    <button data-testid={`approve-${req.request_id}`} onClick={() => act(req.request_id, 'approve', { duration_hours: 24 })}
                      className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition font-medium">Approve</button>
                    <button data-testid={`deny-${req.request_id}`} onClick={() => { const r = prompt('Denial reason:'); act(req.request_id, 'deny', { denial_reason: r || '' }); }}
                      className="px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 transition font-medium">Deny</button>
                  </div>
                )}
                {req.status === 'pending' && adminRole !== 'owner' && req.backup_approval_available && (
                  <div className="shrink-0">
                    <button data-testid={`backup-approve-${req.request_id}`} onClick={() => act(req.request_id, 'approve', { duration_hours: 24 })}
                      className="px-3 py-1.5 bg-yellow-600 text-white text-xs rounded-lg hover:bg-yellow-700 transition font-medium">
                      Approve as Backup
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
